import { DefaultNodeTypes, DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import {
  JSXConvertersFunction,
  RichText as ConvertRichText,
  TextJSXConverter,
} from '@payloadcms/richtext-lexical/react'
import React from 'react'

type NodeTypes = DefaultNodeTypes
export type LexicalRichTextField = {
  root: {
    type: string
    children: {
      type: string
      version: number
      [k: string]: unknown
    }[]
    direction: ('ltr' | 'rtl') | null
    format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
    indent: number
    version: number
  }
  [k: string]: unknown
}

const createDefaultJsxConverters =
  (): JSXConvertersFunction<NodeTypes> =>
  ({ defaultConverters }) => ({
    ...defaultConverters,

    text: ({ node, childIndex, converters, nodesToJSX, parent }) => {
      // Handle newline characters - convert to <br> since translated content from payload will change linebreak node lexical format
      if (node?.text === '\n') {
        return <br key={childIndex} />
      }
      if (node?.$?.color && node?.$?.color === '#4DB5FF') {
        return <span style={{ color: '#4DB5FF' }}>{node.text}</span>
      }
      if (node?.$?.color && node?.$?.color === '#fff') {
        return <span style={{ color: '#fff' }}>{node.text}</span>
      }

      if (node?.$?.color && node?.$?.color === '#A0A9BE') {
        return <span style={{ color: '#A0A9BE' }}>{node.text}</span>
      }

      if (node?.$?.color && node?.$?.color === '#7887AA') {
        return <span style={{ color: '#7887AA' }}>{node.text}</span>
      }

      if (node?.$?.color && node?.$?.color === '#AC9F91') {
        return <span style={{ color: '#AC9F91' }}>{node.text}</span>
      }

      if (node?.$?.color && node?.$?.color === '#7B849B') {
        if (node?.format === 64) {
          return <sup style={{ color: '#7B849B' }}>{node.text}</sup>
        }

        return <span style={{ color: '#7B849B' }}>{node.text}</span>
      }

      if (TextJSXConverter.text && typeof TextJSXConverter.text === 'function') {
        return TextJSXConverter.text({
          node,
          childIndex,
          converters,
          nodesToJSX,
          parent,
        })
      }
    },
  })

export const createCoinSymbolConverters =
  (symbol: string) =>
  ({ defaultConverters }: { defaultConverters: any }) => {
    const baseConverters = createDefaultJsxConverters()({ defaultConverters })
    const baseTextConverter = baseConverters.text as (args: any) => React.ReactNode
    return {
      ...defaultConverters,
      text: (args: any) => {
        const { node } = args
        if (node?.text && typeof node.text === 'string') {
          const modifiedNode = { ...node, text: node.text.replaceAll('{{COIN}}', symbol) }
          return baseTextConverter({ ...args, node: modifiedNode })
        }
        return baseTextConverter(args)
      },
    }
  }

type Props = {
  data: LexicalRichTextField | DefaultTypedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  useNewTemplate?: boolean
  /**
   * Custom converters to override or extend default converters
   * These will be merged with the default converters, with custom converters taking precedence
   */
  customConverters?: JSXConvertersFunction<NodeTypes>
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const { className, customConverters, ...rest } = props

  // If custom converters are provided, merge them with default converters
  const jsxConverters = customConverters
    ? ({ defaultConverters }: { defaultConverters: any }) => {
        const baseConverters = createDefaultJsxConverters()({
          defaultConverters,
        })
        const customConvertersResult = customConverters({ defaultConverters })

        return {
          ...baseConverters,
          ...customConvertersResult,
        }
      }
    : createDefaultJsxConverters()

  return <ConvertRichText className={className} converters={jsxConverters} {...rest} />
}
